import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import DOMPurify from 'dompurify';
import cn from 'classnames';
import Pagination from '@cashew/pagination';
import { Bag, Currency, Customer, Merchant, Store } from '@cashew/assets';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  YAxisProps,
} from 'recharts';
import { useSelector } from 'react-redux';
import { Box, DayBalance, List, PageWithNavigation } from '../../components';
import drawCircleChart from './drawCircleChart';
import './index.css';
import SelectComponent from '../../components/SelectComponent';

const { api, priceFormat } = require('@cashew/utils');

const ns = 'home';

const useMonths = () => {
  const { t } = useTranslation();

  return (cutoff?: number) => (monthIndex: number) => {
    switch (monthIndex) {
      case 1:
        return t('month.january').slice(0, cutoff);
      case 2:
        return t('month.february').slice(0, cutoff);
      case 3:
        return t('month.march').slice(0, cutoff);
      case 4:
        return t('month.april').slice(0, cutoff);
      case 5:
        return t('month.may').slice(0, cutoff);
      case 6:
        return t('month.june').slice(0, cutoff);
      case 7:
        return t('month.july').slice(0, cutoff);
      case 8:
        return t('month.august').slice(0, cutoff);
      case 9:
        return t('month.september').slice(0, cutoff);
      case 10:
        return t('month.october').slice(0, cutoff);
      case 11:
        return t('month.november').slice(0, cutoff);
      case 12:
        return t('month.december').slice(0, cutoff);
      default:
        return '';
    }
  };
};

interface RevenueMonthStat {
  currentYearAmount: number;
  pastYearAmount: number;
  monthIndex: number;
}

interface RevenueResponse {
  status: string;
  data: {
    currencyCode: string;
    cashewDashboardMonthStats: Array<RevenueMonthStat>;
  };
}

const pastYearAmountColor = '#DFF5DF';
const currentYearAmountColor = '#5ECC61';
const axisColor = '#BBB';

interface CustomTooltipProps {
  active?: boolean;
  payload?: { value: number }[];
  label?: number;
  currencyCode: string;
}

const CustomTooltip = ({
  active,
  payload,
  label,
  currencyCode = 'AED',
}: CustomTooltipProps) => {
  const { t } = useTranslation();
  const getMonthByIndex = useMonths();

  if (active && payload && payload.length && label) {
    return (
      <div className="custom-tooltip">
        <p className="label">{`${getMonthByIndex()(label)}`}</p>
        <p className="current">
          {`${t('revenue.year.current')}: `}
          <span>{`${new Intl.NumberFormat('en').format(payload[1].value)} ${currencyCode}`}</span>
        </p>
        <p className="past">
          {`${t('revenue.year.past')}: `}
          <span>{`${new Intl.NumberFormat('en').format(payload[0].value)} ${currencyCode}`}</span>
        </p>
      </div>
    );
  }

  return <></>;
};

CustomTooltip.defaultProps = {
  active: undefined,
  payload: undefined,
  label: undefined,
};

const installmentStatusOptions = ['PAID', 'PENDING', 'CANCELLED'] as const;

type InstallmentStatusOptions = (typeof installmentStatusOptions)[number];

const installmentStatusDropdownOptions: {
  key: InstallmentStatusOptions;
  value: string;
}[] = [
  { key: 'PAID', value: 'Paid' },
  { key: 'PENDING', value: 'Pending' },
  { key: 'CANCELLED', value: 'Cancelled' },
];

const rechartsThousandSeperatorFormatter: YAxisProps['tickFormatter'] = (
  value
) => new Intl.NumberFormat('en').format(value);

/**
 * Login Page RefundPage
 */
export default () => {
  const { t } = useTranslation();
  const getMonthByIndex = useMonths();
  const history = useHistory();
  const { token } = useSelector((state: any) => state.user);
  api.storeAuth(token);
  const [installmentStatus, setInstallmentStatus] = useState<
    'PENDING' | 'CANCELLED' | 'PAID'
  >('PENDING');
  const handleSelect = (key: string) => {
    if (key === 'PENDING' || key === 'CANCELLED' || key === 'PAID') {
      setInstallmentStatus(key);
    } else {
      console.error('Invalid status key');
    }
  };

  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currencyCode, setCurrencyCode] = useState('AED');
  const [totalRows, setTotalRows] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [graphSubsPercent, setSubsPercent] = useState(0);
  const [graphDelayPercent, setDelayPercent] = useState(0);
  const [totalStores, setTotalStores] = useState(0);
  const [totalMerchants, setTotalMerchants] = useState(0);
  const [totalDivideOrders, setTotalDivideOrders] = useState(0);
  const [totalDelayOrders, setTotalDelayOrders] = useState(0);
  const [totalSubscriptionOrders, setTotalSubscriptionOrders] = useState(0);
  const [balanceList, setBalanceList] = useState<Array<any>>([]);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);

  const [revenueStatsByMonth, setRevenueStatsByMonth] = useState<
    RevenueMonthStat[]
  >([]);
  useEffect(() => {
    setIsLoadingBalance(true);
    api
      .get({
        endpoint: 'orders/cashew/dashboard',
        params: {
          size: pageSize,
          page: pageNumber,
          currencyCode,
        },
      })
      .then((resp: any) => {
        setBalanceList(resp.data);
        setTotalRows(resp.totalCount);
        setIsLoadingBalance(false);
      })
      .catch((resp: any) => api.errorHandler({ resp, history }));
  }, [pageNumber, pageSize, currencyCode]);

  useEffect(() => {
    api
      .get({
        endpoint: 'orders/cashew/dashboard/total',
        params: { currencyCode },
      })
      .then((resp: any) => {
        setTotalOrders(resp.data.totalOrders);
        setTotalRevenue(resp.data.totalRevenue);
        setTotalCustomers(resp.data.totalCustomers);
        setTotalSubscriptionOrders(resp.data.totalSubscriptionProducts);
        setTotalStores(resp.data.totalStores);
        setTotalMerchants(resp.data.totalMerchants);
        setTotalDelayOrders(resp.data.totalDelayProducts);
        setTotalDivideOrders(resp.data.totalDivideProducts);
        setSubsPercent(
          (resp.data.totalSubscriptionProducts * 100) /
            (resp.data.totalDelayProducts +
              resp.data.totalDivideProducts +
              resp.data.totalSubscriptionProducts) /
            100
        );
        setDelayPercent(
          (resp.data.totalDelayProducts * 100) /
            (resp.data.totalDelayProducts +
              resp.data.totalDivideProducts +
              resp.data.totalSubscriptionProducts) /
            100
        );
      })
      .catch((resp: any) => api.errorHandler({ resp, history }));
  }, [currencyCode]);

  useEffect(() => {
    api
      .get({
        endpoint: `orders/cashew/revenue?installmentStatus=${installmentStatus}&currencyCode=${currencyCode}`,
      })
      .then((resp: RevenueResponse) => {
        setRevenueStatsByMonth(resp.data.cashewDashboardMonthStats);
      })
      .catch((resp: any) =>
        api.errorHandler({
          resp,
          history,
        })
      );
  }, [installmentStatus, currencyCode]);

  useLayoutEffect(() => {
    const c: any = document.getElementById('myCanvas');
    const context: any = c.getContext('2d');
    c.height = c.width;
    drawCircleChart(
      context,
      150,
      150,
      65,
      graphDelayPercent,
      graphSubsPercent,
      50,
      '#f5f5f5',
      '#ffd11c',
      '#2b90f7'
    );
  }, [graphDelayPercent, graphSubsPercent]);

  return (
    <div className={cn(ns)}>
      <Helmet>
        <title>Overview</title>
      </Helmet>

      <PageWithNavigation>
        <div className="currency-code">
          <SelectComponent
            data={[
              { key: 'AED', value: 'AED' },
              { key: 'SAR', value: 'SAR' },
            ]}
            label="Currency"
            selected={currencyCode}
            onSelect={(e: string) => setCurrencyCode(e)}
          />
        </div>
        <div className={`${ns}__dashboard`}>
          <Box className={`${ns}__dashboard__total-orders`}>
            <div className={`${ns}__circle ${ns}__circle--yellow`}>
              <Bag />
            </div>
            <div>
              <p>Total Orders</p>
              <strong>{totalOrders}</strong>
            </div>
          </Box>
          <Box className={`${ns}__dashboard__total-revenue`}>
            <div className={`${ns}__circle ${ns}__circle--blue`}>
              <Currency />
            </div>
            <div>
              <p>Total GMV</p>
              <strong
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    priceFormat({
                      amount: totalRevenue,
                      currency: `<span>${currencyCode}</span>`,
                    })
                  ),
                }}
              />
            </div>
          </Box>
          <Box className={`${ns}__dashboard__total-customers`}>
            <div className={`${ns}__circle ${ns}__circle--green`}>
              <Customer />
            </div>
            <div>
              <p>Total Customers</p>
              <strong>{totalCustomers}</strong>
            </div>
          </Box>
          <Box className={`${ns}__dashboard__total-stores`}>
            <div className={`${ns}__circle ${ns}__circle--grey`}>
              <Store />
            </div>
            <div>
              <p>Total Stores</p>
              <strong>{totalStores}</strong>
            </div>
          </Box>
          <Box className={`${ns}__dashboard__revenue`}>
            <div className={`${ns}__dashboard__revenue__header`}>
              <div className={`${ns}__dashboard__revenue__header__title`}>
                GMV
              </div>
              <div className={`${ns}__dashboard__revenue__header__legend`}>
                <div
                  className={`${ns}__dashboard__revenue__header__legend__item`}
                >
                  <div
                    style={{ backgroundColor: currentYearAmountColor }}
                    className={`${ns}__dashboard__revenue__header__legend__item__color`}
                  />
                  <div>{t('revenue.year.current')}</div>
                </div>
                <div
                  className={`${ns}__dashboard__revenue__header__legend__item`}
                >
                  <div
                    style={{ backgroundColor: pastYearAmountColor }}
                    className={`${ns}__dashboard__revenue__header__legend__item__color`}
                  />
                  <div>{t('revenue.year.past')}</div>
                </div>
              </div>
              <div style={{ width: '130px' }}>
                <SelectComponent
                  label="Installment status"
                  selected={installmentStatus}
                  onSelect={handleSelect}
                  data={installmentStatusDropdownOptions}
                />
              </div>
            </div>
            {/* @ts-ignore */}
            <ResponsiveContainer
              width="100%"
              height="100%"
              className={`${ns}__dashboard__revenue__chart-container`}
            >
              {/* @ts-ignore */}
              <BarChart
                width={500}
                height={300}
                data={revenueStatsByMonth}
                margin={{
                  top: 30,
                  right: 10,
                  left: 10,
                  bottom: 5,
                }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  horizontal
                  vertical={false}
                />
                <XAxis
                  dataKey="monthIndex"
                  tickFormatter={getMonthByIndex(3)}
                  tickLine={false}
                  stroke={axisColor}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  stroke={axisColor}
                  tickFormatter={rechartsThousandSeperatorFormatter}
                />
                {/* @ts-ignore */}
                <Tooltip
                  content={<CustomTooltip currencyCode={currencyCode} />}
                />
                {/* @ts-ignore */}
                <Bar
                  dataKey="pastYearAmount"
                  fill={pastYearAmountColor}
                  barSize={10}
                  radius={[10, 10, 0, 0]}
                />
                {/* @ts-ignore */}
                <Bar
                  dataKey="currentYearAmount"
                  fill={currentYearAmountColor}
                  barSize={10}
                  radius={[10, 10, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
          <Box className={`${ns}__dashboard__totalMerchants`}>
            <div className={`${ns}__circle ${ns}__circle--red`}>
              <Merchant />
            </div>
            <div>
              <p>Total Merchants</p>
              <strong>{totalMerchants}</strong>
            </div>
          </Box>
          <Box className={`${ns}__dashboard__orders`}>
            <div>
              <p>Orders</p>
              <canvas
                id="myCanvas"
                style={{
                  height: '100%',
                }}
              >
                Your browser does not support the HTML5 canvas tag.
              </canvas>
              <div className={`${ns}__delayNumbers`}>
                <strong>{totalSubscriptionOrders}</strong>
                <div className={`${ns}__legendWrapper`}>
                  <span>Subscription</span>
                  <div
                    style={{
                      height: '12px',
                      width: '12px',
                      backgroundColor: '#2b90f7',
                      marginLeft: '10px',
                    }}
                  />
                </div>
              </div>
              <div className={`${ns}__divideNumbers`}>
                <strong>{totalDelayOrders}</strong>
                <div className={`${ns}__legendWrapper`}>
                  <div
                    style={{
                      height: '12px',
                      width: '12px',
                      backgroundColor: '#ffd11c',
                      marginRight: '10px',
                    }}
                  />
                  <span>Delay</span>
                </div>
              </div>
              <div className={`${ns}__subscriptionNumbers`}>
                <strong>{totalDivideOrders}</strong>
                <div className={`${ns}__legendWrapper`}>
                  <div
                    style={{
                      height: '12px',
                      width: '12px',
                      backgroundColor: '#f5f5f5',
                      marginRight: '10px',
                    }}
                  />
                  <span>Divide</span>
                </div>
              </div>
            </div>
          </Box>
          <Box className={`${ns}__dashboard__list`}>
            <header>
              <ul>
                <li>Date</li>
                <li>Orders</li>
                <li>Refunds</li>
                <li>Total Amount</li>
              </ul>
            </header>
            <List
              data={balanceList}
              component={({ data }: any) =>
                DayBalance({ data: { ...data, currencyCode } })
              }
              isLoading={isLoadingBalance}
            />
            <Pagination
              records={totalRows}
              pageSize={pageSize}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              changePageSize={setPageSize}
            />
          </Box>
        </div>
      </PageWithNavigation>
    </div>
  );
};
